import React, { useState } from 'react';
import { FileText, AlertCircle, CheckCircle2, Flag, ShieldCheck } from 'lucide-react';
import { Form1008Data, ValidationErrors } from '../types';
import { Accordion } from './ui/Accordion';
import { Section1 } from './sections/Section1';
import { Section2 } from './sections/Section2';
import { Section3 } from './sections/Section3';
import { Section4 } from './sections/Section4';
import { LoanSnapshot } from './LoanSnapshot';
import { UnderwriterNotes } from './UnderwriterNotes';
import { generateForm1008Pdf } from '../utils/generateForm1008Pdf';
const initialData: Form1008Data = {
  loanNumber: 'MM-2026-0488217',
  investorLoanNumber: 'INV-7741-DSCR',
  program: 'DSCR Investor — 30 Yr Fixed',
  decisionStage: 'underwriting',
  borrowerName: 'Riverstone Holdings LLC — Marcus T. Delgado, Guarantor',
  totalBorrowers: 1,
  propertyAddress: '4827 Magnolia Court, Tampa, FL 33607',
  occupancy: 'investment',
  salesPrice: 415000,
  appraisedValue: 420000,
  propertyType: '1unit',
  projectClassification: '',
  agency: 'fannie',
  propertyRights: 'feeSimple',
  propertyKind: '',
  manufacturedWidth: '',
  projectName: '',
  condoProjectId: '',
  loanType: 'conventional',
  amortizationType: 'fixedMonthly',
  armType: '',
  amortOther: '',
  loanPurpose: 'purchase',
  lienPosition: 'first',
  subordinateAmount: 0,
  loanAmount: 311250,
  noteRate: 7.625,
  loanTermMonths: 360,
  mortgageOriginator: 'correspondent',
  tempBuydown: false,
  buydownTerms: '',
  brokerName: 'Madison Mortgage, LLC — NMLS #1029384',
  underwriterName: 'Danielle R. Okafor',
  appraiserName: 'Theodore J. Brandt',
  appraiserLicense: 'FL RD-7741',
  appraisalCompany: 'Gulfshore Valuation Partners, Inc.',
  qualMethod: 'dscr',
  income1: 0,
  income2: 0,
  income3: 0,
  income4: 0,
  incomeOther: 0,
  rentalIncomeSubject: 3200,
  netRentalOther: 0,
  selfEmployed: true,
  subordinatePI: 0,
  homeownersInsurance: 145,
  supplementalInsurance: 75,
  propertyTaxes: 430,
  mortgageInsurance: 0,
  hoaDues: 0,
  otherHousing: 0,
  allOtherPayments: 0,
  qualRateType: 'rateUsed',
  qualRate: 7.625,
  appraisalFormNumber: '1004 + 1007',
  fundsRequired: 116480,
  verifiedAssets: 168400,
  escrowTI: true,
  monthsReserves: 8,
  riskMethod: 'manual',
  interestedPartyContrib: 3,
  affordableHousing: false,
  homeownershipEd: false,
  ausType: '',
  ausRecommendation: 'N/A — Manually underwritten (Non-QM DSCR)',
  duCaseId: '',
  lpaDocClass: '',
  repCreditScore: 762,
  propertyReviewLevel: 'exteriorInterior',
  underwriterComments: 'Non-QM DSCR investor program. Subject qualified on property cash flow per Form 1007 market rent of $3,200; personal income not used. DSCR of 1.12 meets the 1.10 program floor. Title vested in Riverstone Holdings LLC with Marcus T. Delgado as personal guarantor (762 mid). 8 months PITIA reserves verified. LTV 75.0% within 80% program max for purchase. No subordinate financing. Interested-party contributions limited to 3%.',
  sellerName: 'Madison Mortgage, LLC',
  sellerAddress: '1820 Commerce Park Drive, Suite 300, Charlotte, NC 28210',
  sellerNumber: '29104',
  sellerLoanNumber: 'MM-2026-0488217',
  contactName: 'Danielle R. Okafor',
  contactTitle: 'Senior Underwriter',
  contactPhone: '(704) 555-0142',
  contactPhoneAlt: '(704) 555-0177'
};
const REQUIRED_FIELDS = {
  section1: ['borrowerName', 'totalBorrowers', 'propertyAddress', 'salesPrice', 'appraisedValue', 'occupancy', 'propertyType', 'propertyRights'],
  section2: ['loanAmount', 'noteRate', 'loanTermMonths', 'loanType', 'amortizationType', 'loanPurpose', 'lienPosition', 'mortgageOriginator'],
  section3: ['underwriterName', 'appraiserName', 'repCreditScore', 'qualRate', 'homeownersInsurance', 'propertyTaxes', 'underwriterComments'],
  section4: ['sellerName', 'contactName', 'contactPhone']
};
export function Form1008() {
  const [data, setData] = useState<Form1008Data>(initialData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [openSections, setOpenSections] = useState({
    s1: true,
    s2: true,
    s3: true,
    s4: false
  });
  const [status, setStatus] = useState<'idle' | 'error' | 'success'>('idle');
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportError, setExportError] = useState('');
  const [decision, setDecision] = useState<'none' | 'suspended' | 'declined' | 'approved'>('none');
  const handleChange = (field: keyof Form1008Data, value: any) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (status !== 'idle') setStatus('idle');
  };
  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const countCompleted = (fields: string[]) => {
    return fields.filter(f => {
      const val = data[f as keyof Form1008Data];
      return val !== '' && val !== null && val !== undefined;
    }).length;
  };
  const handleGenerate = async () => {
    const newErrors: ValidationErrors = {};
    let hasErrors = false;
    Object.values(REQUIRED_FIELDS).flat().forEach(field => {
      const val = data[field as keyof Form1008Data];
      if (val === '' || val === null || val === undefined) {
        newErrors[field as keyof Form1008Data] = 'Required for Form 1008';
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setErrors(newErrors);
      setExportError('');
      setOpenSections({
        s1: true,
        s2: true,
        s3: true,
        s4: true
      });
      setStatus('error');
      return;
    }
    setIsGenerating(true);
    setExportError('');
    try {
      await Promise.resolve(generateForm1008Pdf(data));
      setErrors({});
      setStatus('success');
    } catch {
      setStatus('idle');
      setExportError('The Form 1008 PDF could not be generated. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  const totalErrors = Object.keys(errors).length;
  return <div className="max-w-[1600px] mx-auto p-2 md:p-4 flex flex-col lg:flex-row gap-4 items-start" data-id="element-2">
      <div className="flex-1 min-w-0 w-full bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-id="element-3">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-4">
          <div className="flex items-center space-x-3" data-id="element-5">
            <div className="w-8 h-8 bg-slate-100 text-slate-600 rounded flex items-center justify-center shrink-0" data-id="element-6">
              <FileText className="w-4 h-4" data-id="element-7" />
            </div>
            <div className="flex items-center gap-4" data-id="element-8">
              <h1 className="text-lg font-bold text-slate-900" data-id="element-9">
                Uniform Underwriting & Transmittal Summary
              </h1>
              <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 tracking-wider uppercase" data-id="element-10">
                Fannie 1008 · Freddie 1077
              </div>
            </div>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-3" data-id="element-11">
            <div className="flex flex-wrap gap-2" data-id="element-12">
              <button type="button" onClick={() => setDecision('suspended')} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2" data-id="element-13">
                <Flag className="w-4 h-4 text-slate-500" data-id="element-14" />
                Suspend
              </button>
              <button type="button" onClick={() => setDecision('declined')} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-600 font-medium rounded-lg shadow-sm transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2" data-id="element-15">
                Decline
              </button>
              <button type="button" onClick={() => setDecision('approved')} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2" data-id="element-16">
                <ShieldCheck className="w-4 h-4" data-id="element-17" />
                Approve
              </button>
            </div>
            <button type="button" onClick={handleGenerate} disabled={isGenerating} className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70" data-id="element-18">
              <FileText className="w-4 h-4" data-id="element-19" />
              {isGenerating ? 'Generating PDF…' : 'Generate Form 1008'}
            </button>
          </div>
        </div>

        {/* Banners */}
        {exportError && <div className="bg-red-50 border-b border-red-200 p-4 flex items-start space-x-3" data-id="element-20">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-21" />
            <div data-id="element-22">
              <h3 className="text-sm font-medium text-red-800" data-id="element-23">
                PDF export unavailable
              </h3>
              <p className="text-sm text-red-700 mt-1" data-id="element-24">{exportError}</p>
            </div>
          </div>}
        {decision !== 'none' && <div className={`border-b p-4 flex items-start space-x-3 ${decision === 'approved' ? 'bg-green-50 border-green-200' : decision === 'declined' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-25">
            {decision === 'approved' ? <ShieldCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-26" /> : decision === 'declined' ? <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-27" /> : <Flag className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-28" />}
            <div data-id="element-29">
              <h3 className={`text-sm font-medium ${decision === 'approved' ? 'text-green-800' : decision === 'declined' ? 'text-red-800' : 'text-amber-800'}`} data-id="element-30">
                {decision === 'approved' ? 'Loan Approved' : decision === 'declined' ? 'Loan Declined' : 'Loan Suspended'}
              </h3>
              <p className={`text-sm mt-1 ${decision === 'approved' ? 'text-green-700' : decision === 'declined' ? 'text-red-700' : 'text-amber-700'}`} data-id="element-31">
                {decision === 'approved' ? 'This file has been marked approved. Underwriting decision recorded on the loan.' : decision === 'declined' ? 'This file has been declined. A decision reason should be documented before adverse-action notice.' : 'This file has been suspended pending additional documentation or conditions.'}
              </p>
            </div>
          </div>}
        {status === 'error' && <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-start space-x-3" data-id="element-32">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-33" />
            <div data-id="element-34">
              <h3 className="text-sm font-medium text-amber-800" data-id="element-35">
                Validation Error
              </h3>
              <p className="text-sm text-amber-700 mt-1" data-id="element-36">
                {totalErrors} required fields must be completed before the Form
                1008 can be generated.
              </p>
            </div>
          </div>}
        {status === 'success' && <div className="bg-green-50 border-b border-green-200 p-4 flex items-start space-x-3" data-id="element-37">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-38" />
            <div data-id="element-39">
              <h3 className="text-sm font-medium text-green-800" data-id="element-40">
                Ready to Generate
              </h3>
              <p className="text-sm text-green-700 mt-1" data-id="element-41">
                All required fields complete — Form 1008 is ready to generate
                from loan-level data.
              </p>
            </div>
          </div>}

        {/* Form Content */}
        <div className="px-6 py-2 bg-white" data-id="element-42">
          <Accordion title="Borrower and Property Information" numeral="I" isOpen={openSections.s1} onToggle={() => toggleSection('s1')} requiredCount={REQUIRED_FIELDS.section1.length} completedCount={countCompleted(REQUIRED_FIELDS.section1)} data-id="element-43">
            <Section1 data={data} onChange={handleChange} errors={errors} data-id="element-44" />
          </Accordion>

          <Accordion title="Mortgage Information" numeral="II" isOpen={openSections.s2} onToggle={() => toggleSection('s2')} requiredCount={REQUIRED_FIELDS.section2.length} completedCount={countCompleted(REQUIRED_FIELDS.section2)} data-id="element-45">
            <Section2 data={data} onChange={handleChange} errors={errors} data-id="element-46" />
          </Accordion>

          <Accordion title="Underwriting Information" numeral="III" isOpen={openSections.s3} onToggle={() => toggleSection('s3')} requiredCount={REQUIRED_FIELDS.section3.length} completedCount={countCompleted(REQUIRED_FIELDS.section3)} data-id="element-47">
            <Section3 data={data} onChange={handleChange} errors={errors} data-id="element-48" />
          </Accordion>

          <Accordion title="Seller and Contact Information" numeral="IV" isOpen={openSections.s4} onToggle={() => toggleSection('s4')} requiredCount={REQUIRED_FIELDS.section4.length} completedCount={countCompleted(REQUIRED_FIELDS.section4)} data-id="element-49">
            <Section4 data={data} onChange={handleChange} errors={errors} data-id="element-50" />
          </Accordion>
        </div>
      </div>

      <aside className="w-full lg:w-[340px] shrink-0 space-y-6" data-id="element-51">
        <LoanSnapshot data={data} data-id="element-52" />
        <UnderwriterNotes data-id="element-53" />
      </aside>
    </div>;
}